import api from "./api";
import { resolveRoleName } from "../utils/auth";

export const normalizeFloor = (dto) => ({
  // Support multiple API shapes: snake_case, camelCase, legacy
  floor_id: dto?.floor_id ?? dto?.id,
  floor_code: dto?.floor_code ?? dto?.floorCode ?? dto?.code ?? "",
  name: dto?.name ?? "",
  building_id: dto?.building_id ?? dto?.buildingId ?? null,
  building: dto?.building ?? null,
  created_by: dto?.created_by ?? dto?.createdBy ?? null,
  updated_by: dto?.updated_by ?? dto?.updatedBy ?? null,
  creator: dto?.creator ?? null,
  updater: dto?.updater ?? null,
  rows: dto?.rows ?? 0,
  columns: dto?.columns ?? 0,
  // Storey number: 0 = ground, negative for basements. Null on older floors,
  // which fall back to ordering by code.
  level: dto?.level ?? null,
  created_at: dto?.created_at ?? dto?.createdAt ?? null,
  updated_at: dto?.updated_at ?? dto?.updatedAt ?? null,
});

const extractFloorArray = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const normalizeRoleKey = (value) =>
  String(value || "")
    .toUpperCase()
    .replace(/[^A-Z]/g, "");

const getCurrentAuthUser = () => {
  try {
    const raw = sessionStorage.getItem("auth");
    const auth = raw ? JSON.parse(raw) : null;
    if (auth?.user) return auth.user;
  } catch {}

  try {
    const raw = localStorage.getItem("auth");
    const auth = raw ? JSON.parse(raw) : null;
    if (auth?.user) return auth.user;
  } catch {}

  return null;
};

async function getCurrentUserRoleKey() {
  const user = getCurrentAuthUser();
  if (!user) return "";
  const roleName = await resolveRoleName(user);
  return normalizeRoleKey(roleName);
}

export async function getFloorsPaginated({
  page = 1,
  limit = 10,
  search = "",
} = {}) {
  const query = new URLSearchParams();
  if (page != null) query.append("page", String(page));
  if (limit != null) query.append("limit", String(limit));
  if (String(search || "").trim())
    query.append("search", String(search).trim());

  const qs = query.toString();
  const roleKey = await getCurrentUserRoleKey();
  const endpoint =
    roleKey === "SUPERADMIN" ? "/floor" : (
      "/hierarchical-view/relation/floor/bulk"
    );
  const res = await api.get(`${endpoint}${qs ? `?${qs}` : ""}`);
  const data = extractFloorArray(res);
  const meta =
    res?.meta && typeof res.meta === "object" ?
      res.meta
    : {
        total: data.length,
        page: Number(page) || 1,
        limit: Number(limit) || 10,
        totalPages: 1,
      };

  return {
    data: data.map(normalizeFloor),
    meta: {
      total: Number(meta.total) || 0,
      page: Number(meta.page) || 1,
      limit: Number(meta.limit) || Number(limit) || 10,
      totalPages: Number(meta.totalPages) || 1,
    },
  };
}

export async function getFloorsByBuildingId(buildingId) {
  const data = await api.get(`/floor/buildingId/${buildingId}`);
  return extractFloorArray(data).map(normalizeFloor);
}

/**
 * Requests for the same floor that overlap in time, sharing one round trip.
 *
 * Opening a floor mounts three readers of this payload at once — the page
 * (details and building), the design layout, and the desk grid — which is three
 * identical GETs in the same tick.
 *
 * Deliberately NOT a TTL cache. Every later read here is a deliberate refresh
 * after a write (a save, a merge, a desk added or removed) and has to see the
 * server's new state; caching would serve it the state it just changed. Only
 * concurrent callers share, and the entry is dropped as soon as it settles.
 */
const _floorRelationsInFlight = new Map();

// Fetch floor with building and rooms (with tables)
export async function getFloorWithRelations(id) {
  const key = String(id);
  let request = _floorRelationsInFlight.get(key);

  if (!request) {
    request = api
      .get(`/floor/${id}/with-relations`)
      .finally(() => _floorRelationsInFlight.delete(key));
    _floorRelationsInFlight.set(key, request);
  }

  // Normalized per caller: the raw payload is shared, the object graph is not,
  // so no reader can reach another's rooms or tables.
  return normalizeFloorWithRelations((await request) || null);
}

// Normalize floor with relations
function normalizeFloorWithRelations(dto) {
  if (!dto || typeof dto !== "object") return dto;
  const base = normalizeFloor(dto);
  // Prefer 'rooms'; map from 'room' if present
  const rawRooms =
    Array.isArray(dto.rooms) ? dto.rooms
    : Array.isArray(dto.room) ? dto.room
    : [];
  const rooms = rawRooms.map((r) => {
    const rawTables =
      Array.isArray(r?.tables) ? r.tables
      : Array.isArray(r?.table) ? r.table
      : [];
    const tables = rawTables.map((t) => ({
      table_id: t?.table_id ?? t?.id,
      table_code: t?.table_code ?? t?.tableCode ?? t?.code,
      name: t?.name ?? "",
      room_id: t?.room_id ?? t?.roomId ?? r?.room_id ?? r?.id,
      capacity: Number(t?.capacity) || 0,
      orientation: t?.orientation ?? "",
      // map table_job -> tableJobs [{ job_id, job }]
      tableJobs: (Array.isArray(t?.tableJobs) ? t.tableJobs
      : Array.isArray(t?.table_job) ? t.table_job
      : []
      ).map((tj) => ({
        job_id: tj?.job_id ?? tj?.job?.job_id ?? null,
        job: tj?.job ?? null,
        assigned_at: tj?.assigned_at ?? null,
      })),
    }));
    return {
      room_id: r?.room_id ?? r?.id,
      room_code: r?.room_code ?? r?.roomCode ?? r?.code,
      name: r?.name ?? "",
      floor_id: r?.floor_id ?? r?.floorId ?? base.floor_id ?? null,
      cellType: r?.cellType ?? r?.cell_type ?? "",
      row: r?.row ?? r?.cell_row ?? null,
      column: r?.column ?? r?.cell_column ?? null,
      // Layout geometry. A room can span a rectangle of cells; the cells it
      // swallowed still exist and point back at it, which is what makes a merge
      // reversible. Dropping any of these here would make a saved merge
      // invisible to the designer — the same silent field-list loss the server
      // side hit three times.
      row_span: Number(r?.row_span ?? r?.rowSpan ?? 1) || 1,
      col_span: Number(r?.col_span ?? r?.colSpan ?? 1) || 1,
      absorbed_by_room_id: r?.absorbed_by_room_id ?? r?.absorbedByRoomId ?? null,
      space_type_id:
        r?.space_type_id ?? r?.spaceTypeId ?? r?.space_type?.space_type_id ?? null,
      space_type: r?.space_type ?? null,
      room_template_id: r?.room_template_id ?? r?.roomTemplateId ?? null,
      tables,
    };
  });
  const building = dto.building ?? null;
  return {
    ...base,
    // The building's own cell layout is the authority on where the cores are,
    // so the designer needs it to tell a floor that has drifted from it.
    building:
      building ?
        { ...building, building_cell: building.building_cell ?? building.cells ?? [] }
      : null,
    rows: base.rows || Number(building?.rows) || 0,
    columns: base.columns || Number(building?.columns) || 0,
    rooms,
  };
}

/**
 * Merge a rectangle of cells into one room, or split one back apart.
 *
 * Spans of 1 un-merge. Absorbed cells are never deleted, so this is reversible
 * and no table or job assignment is lost either way.
 */
export async function mergeFloorCells(
  floorId,
  { row, column, row_span, col_span, space_type_id },
) {
  const body = {
    row: Number(row),
    column: Number(column),
    row_span: Number(row_span) || 1,
    col_span: Number(col_span) || 1,
  };
  if (space_type_id !== undefined) body.space_type_id = space_type_id;
  return api.post(`/floor/${floorId}/merge-cells`, body);
}

export async function createFloor(payload) {
  const body = {
    building_id: Number(payload.building_id),
    // API expects camelCase floorCode per latest requirement
    floorCode:
      payload.floorCode !== undefined ? String(payload.floorCode).trim()
      : payload.floor_code !== undefined ? String(payload.floor_code).trim()
      : undefined,
    name: payload.name !== undefined ? String(payload.name).trim() : undefined,
  };
  const res = await api.post("/floor", body);
  return res ?
      normalizeFloor(res)
    : normalizeFloor({ id: Math.floor(Math.random() * 1000000), ...body });
}

export async function patchFloor(id, payload) {
  const body = {};
  if (payload.floorCode !== undefined)
    body.floorCode = String(payload.floorCode).trim();
  else if (payload.floor_code !== undefined)
    body.floorCode = String(payload.floor_code).trim();
  if (payload.name !== undefined) body.name = String(payload.name).trim();
  if (payload.building_id !== undefined)
    body.building_id = Number(payload.building_id);
  const res = await api.patch(`/floor/${id}`, body);
  return res ? normalizeFloor(res) : normalizeFloor({ id, ...body });
}

export async function deleteFloor(id) {
  return api.delete(`/floor/${id}`);
}

export async function deleteFloorsBulk(ids = []) {
  await Promise.all(ids.map((i) => deleteFloor(i)));
}
